// Classifies a transfer-upload error into either a customer-correctable
// "validation_friction" outcome or a system-side "failed" outcome. The output
// drives parallel emission of the new MN-4800 events
// (`transfer_upload_validation_friction`, `transfer_upload_failed`) alongside
// legacy `image_upload_*` events. See tmp/MN-4800/plan.md for the full design.
//
// Pure module — no I/O, no event emission. Callers handle the actual emit.

// Customer-correctable error_type values produced by the DTF/transfer pipeline.
// Kept locally rather than importing the Borneo-specific upload-error.js
// VALIDATION_ERROR_TYPES so the classifier stays neutral and reusable.
const FRICTION_ERROR_TYPES = new Set([
  "image_validation_error",
  "backend_file_validation",
  "resolution_gate",
  "min_print_size",
  "max_print_size",
  "max_print_size_print_as_is",
  "invalid_height",
  "invalid_width",
  "invalid_gang_sheet",
  "negative_values",
  "upscale_threshold_exceeded"
]);

// Legacy event names that always indicate a system failure regardless of
// error_type. `image_upload_error` is the noisy fallback and is handled
// separately in classifyTransferUploadError.
const FAILED_EVENT_NAMES = new Set([
  "image_fetch_error",
  "image_upload_network_error",
  "image_upload_process_error"
]);

const HINT_BY_ERROR_TYPE = Object.freeze({
  min_print_size: "increase_size",
  max_print_size: "reduce_size",
  max_print_size_print_as_is: "reduce_size_or_use_gangsheet",
  invalid_height: "fix_dimensions",
  invalid_width: "fix_dimensions",
  invalid_gang_sheet: "fix_dimensions",
  negative_values: "fix_dimensions",
  upscale_threshold_exceeded: "use_higher_resolution_image",
  resolution_gate: "use_higher_resolution_image"
});

const STAGE_BY_ERROR_TYPE = Object.freeze({
  image_processing_error: "processing",
  backend_processing_error: "processing",
  current_pdp_date_json_error: "processing",
  product_prices_json_error: "processing"
});

const STAGE_BY_EVENT_NAME = Object.freeze({
  image_fetch_error: "fetch",
  image_upload_network_error: "network",
  image_upload_process_error: "processing"
});

// `backend_file_validation` is a coarse error_type that covers size, type,
// page-count, and integrity rejections. Refine to a specific hint by reading
// the backend's error message; fall back to a generic friction hint.
const refineBackendFileValidationHint = errorMessage => {
  if (typeof errorMessage !== "string") return "use_different_file";
  if (/file is too large|maximum allowed size/i.test(errorMessage)) return "reduce_file_size";
  if (/not.*supported|invalid.*extension/i.test(errorMessage)) return "use_supported_file_type";
  if (/multiple.*page|page.*count|pdf.*page/i.test(errorMessage)) return "use_different_file";
  if (/header|corrupted|invalid.*image/i.test(errorMessage)) return "use_different_file";
  return "use_different_file";
};

// Property names use snake_case to match the wire-format event payload
// emitted to Mixpanel. eslint disables intentional.
/* eslint-disable camelcase */
const friction = customer_correction_hint => ({
  kind: "validation_friction",
  customer_correction_hint
});

const failed = failure_stage => ({ kind: "failed", failure_stage });
/* eslint-enable camelcase */

// Maps a legacy error event/type pair to one of:
//   { kind: "validation_friction", customer_correction_hint }
//   { kind: "failed",              failure_stage }
//
// The output is the contract for `transfer_upload_validation_friction` and
// `transfer_upload_failed` payload assembly. Returning a discriminated union
// makes mis-classification a type/runtime error at the emit site instead of a
// silent dashboard contamination.
//
// @param {object} input
// @param {string} [input.errorEvent] legacy event name about to be emitted
// @param {string} [input.errorType]  error_type from response or context
// @param {string} [input.errorMessage] human-readable error message
// @returns {{kind: "validation_friction", customer_correction_hint: string}
//   | {kind: "failed", failure_stage: string}}
// eslint-disable-next-line import/prefer-default-export
export const classifyTransferUploadError = ({ errorEvent, errorType, errorMessage } = {}) => {
  // Step 1 — explicit failure-stage by event name.
  if (errorEvent && FAILED_EVENT_NAMES.has(errorEvent)) {
    return failed(STAGE_BY_EVENT_NAME[errorEvent]);
  }

  // Step 2 — explicit failure-stage by error_type (covers backend_processing_error
  // which returns HTTP 422 but is a system failure, not friction).
  if (errorType && STAGE_BY_ERROR_TYPE[errorType]) {
    return failed(STAGE_BY_ERROR_TYPE[errorType]);
  }

  // Step 3 — explicit friction by event name.
  if (errorEvent === "image_upload_unsupported_file") {
    return friction("use_supported_file_type");
  }
  if (errorEvent === "image_upload_rejected_validation") {
    return friction("use_higher_resolution_image");
  }

  // Step 4 — friction by error_type.
  if (errorType === "backend_file_validation") {
    return friction(refineBackendFileValidationHint(errorMessage));
  }
  if (errorType && HINT_BY_ERROR_TYPE[errorType]) {
    return friction(HINT_BY_ERROR_TYPE[errorType]);
  }
  if (errorType && FRICTION_ERROR_TYPES.has(errorType)) {
    return friction("unknown_friction");
  }

  // Step 5 — legacy `image_upload_error` fallback. Treat as failed/unknown
  // unless the error_type still classifies as friction (covered above).
  return failed("unknown");
};
